import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/authStore';

const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1';

export const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

interface RetryableConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

let refreshPromise: Promise<string | null> | null = null;

const refreshTokens = async (): Promise<string | null> => {
  const refresh = useAuthStore.getState().refreshToken;
  if (!refresh) return null;

  try {
    const response = await axios.post(`${BASE_URL}/auth/refresh/`, { refresh });
    const newAccess = response.data.access as string;
    const newRefresh = (response.data.refresh as string | undefined) ?? refresh;
    useAuthStore.getState().setTokens(newAccess, newRefresh);
    return newAccess;
  } catch {
    useAuthStore.getState().logout();
    return null;
  }
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetryableConfig | undefined;
    if (!original || error.response?.status !== 401 || original._retry) {
      return Promise.reject(error);
    }
    original._retry = true;

    refreshPromise = refreshPromise ?? refreshTokens();
    const newToken = await refreshPromise;
    refreshPromise = null;

    if (!newToken) {
      return Promise.reject(error);
    }

    original.headers = { ...(original.headers ?? {}), Authorization: `Bearer ${newToken}` };
    return apiClient(original);
  },
);
